import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BaseTool.css';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';

const Toolbox = () => {
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Free AI Writing Tools – Shorten, Expand, Rewrite & Match Styles | Book Witch",
        "description": "Explore free AI-powered writing tools: shorten content, paraphrase, expand text, or match any writing style. No signup needed – try them now with Book Witch.",
        "url": "https://bookwitch.io/tools",
        "mainEntity": {
            "@type": "SoftwareApplication",
            "applicationCategory": "WebApplication",
            "name": "Book Witch AI Writing Tools",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
            },
            "operatingSystem": "Web browser",
            // Note: Remove hardcoded aggregateRating as it should come from actual user ratings
            // "aggregateRating": {
            //     "@type": "AggregateRating", 
            //     "ratingValue": "4.8",
            //     "ratingCount": "156"
            // }
        },
        "hasPart": [
            {
                "@type": "WebApplication",
                "name": "Text Shortener",
                "description": "Cut down long texts without losing meaning. Great for summaries, emails, or social media.",
                "url": "https://bookwitch.io/tools/shorten"
            },
            {
                "@type": "WebApplication",
                "name": "Paraphrasing Tool",
                "description": "Rewrite your text with a new voice. Choose from 15+ tones, all plagiarism-free.",
                "url": "https://bookwitch.io/tools/paraphrase"
            },
            {
                "@type": "WebApplication",
                "name": "Content Expander",
                "description": "Add clarity, structure, and examples to your content. Perfect for underdeveloped ideas.",
                "url": "https://bookwitch.io/tools/expand"
            },
            {
                "@type": "WebApplication",
                "name": "Style Matching",
                "description": "Adapt your content to any writing style. Match tone and rhythm from a sample.",
                "url": "https://bookwitch.io/tools/style"
            }
        ]
    };
    
    return (
        <>
            <Helmet>
                <title>Free AI Writing Tools – Shorten, Expand, Rewrite & Match Styles | Book Witch</title>
                <meta name="description" content="Explore free AI-powered writing tools: shorten content, paraphrase, expand text, or match any writing style. No signup needed – try them now with Book Witch." />

                <meta name="keywords" content="free AI writing tools, AI text tools, paraphrasing tool, content expansion tool, style matching, text shortener" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content="Free AI Writing Tools – Paraphrasing, Expansion, Style Matching & More | Book Witch" />
                <meta property="og:description" content="Discover a collection of free AI tools designed to help you improve your writing instantly. No signup required." />
                <meta property="og:url" content="https://bookwitch.io/tools" />

                <link rel="canonical" href="https://bookwitch.io/tools" />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>

            <Navbar />
            <div className="base-tool">
                <div className="bt-header">
                    <h1>Free AI Writing Tools – Paraphrasing, Expansion, Style Matching & More</h1>
                    <p>Discover a collection of <strong>free AI tools</strong> designed to help you improve your writing instantly. Whether you're editing, rewriting, or generating new content, each tool is crafted to solve a specific writing task — fast, smart, and effortlessly.</p>
                    <p>All tools are powered by Book Witch's advanced language engine.</p>
                </div>

                <div className="bt-tools-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                    gap: '30px',
                    margin: '50px 0' 
                }}>
                    <div className="bt-tool-card" style={{ 
                        padding: '30px', 
                        borderRadius: 'var(--border-radius)', 
                        background: '#ebf8ff',
                        border: '1px solid #bee3f8',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.location.href = '/tools/shorten'}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}>
                        <span className="material-symbols-outlined" style={{ 
                            fontSize: '40px', 
                            color: '#2b6cb0', 
                            marginBottom: '15px',
                            display: 'block'
                        }}>
                            content_cut
                        </span>
                        <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#2b6cb0' }}>Text Shortener</h3>
                        <p style={{ marginBottom: '20px' }}>Cut down long texts without losing meaning. Great for summaries, emails, or social media.</p>
                        <Link to="/tools/shorten" style={{ 
                            color: '#2b6cb0', 
                            textDecoration: 'none', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center' 
                        }}>
                            Use the AI Shortener
                            <span className="material-symbols-outlined" style={{ marginLeft: '8px', fontSize: '18px' }}>arrow_forward</span>
                        </Link>
                    </div>

                    <div className="bt-tool-card" style={{ 
                        padding: '30px', 
                        borderRadius: 'var(--border-radius)', 
                        background: '#fffff0',
                        border: '1px solid #fefcbf',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.location.href = '/tools/paraphrase'}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}>
                        <span className="material-symbols-outlined" style={{ 
                            fontSize: '40px', 
                            color: '#975a16', 
                            marginBottom: '15px',
                            display: 'block'
                        }}>
                            autorenew
                        </span>
                        <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#975a16' }}>Paraphrasing Tool</h3>
                        <p style={{ marginBottom: '20px' }}>Rewrite your text with a new voice. Choose from 15+ tones, all plagiarism-free.</p>
                        <Link to="/tools/paraphrase" style={{ 
                            color: '#975a16', 
                            textDecoration: 'none', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center' 
                        }}>
                            Try the Paraphraser
                            <span className="material-symbols-outlined" style={{ marginLeft: '8px', fontSize: '18px' }}>arrow_forward</span>
                        </Link>
                    </div>

                    <div className="bt-tool-card" style={{ 
                        padding: '30px', 
                        borderRadius: 'var(--border-radius)', 
                        background: '#f0fff4',
                        border: '1px solid #c6f6d5',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.location.href = '/tools/expand'}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}>
                        <span className="material-symbols-outlined" style={{ 
                            fontSize: '40px', 
                            color: '#2f855a', 
                            marginBottom: '15px',
                            display: 'block'
                        }}>
                            text_increase
                        </span>
                        <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#2f855a' }}>Content Expander</h3>
                        <p style={{ marginBottom: '20px' }}>Add clarity, structure, and examples to your content. Perfect for underdeveloped ideas.</p>
                        <Link to="/tools/expand" style={{ 
                            color: '#2f855a', 
                            textDecoration: 'none', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center' 
                        }}>
                            Use the Expander
                            <span className="material-symbols-outlined" style={{ marginLeft: '8px', fontSize: '18px' }}>arrow_forward</span>
                        </Link>
                    </div>

                    <div className="bt-tool-card" style={{ 
                        padding: '30px', 
                        borderRadius: 'var(--border-radius)', 
                        background: '#faf5ff',
                        border: '1px solid #e9d8fd',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.location.href = '/tools/style'}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}>
                        <span className="material-symbols-outlined" style={{ 
                            fontSize: '40px', 
                            color: '#6b46c1', 
                            marginBottom: '15px',
                            display: 'block'
                        }}>
                            format_paint
                        </span>
                        <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#6b46c1' }}>Style Matching</h3>
                        <p style={{ marginBottom: '20px' }}>Adapt your content to any writing style. Match tone and rhythm from a sample.</p>
                        <Link to="/tools/style" style={{ 
                            color: '#6b46c1', 
                            textDecoration: 'none', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center' 
                        }}>
                            Try the Style Matcher
                            <span className="material-symbols-outlined" style={{ marginLeft: '8px', fontSize: '18px' }}>arrow_forward</span>
                        </Link>
                    </div>
                </div>

                <section className="bt-section">
                    <div className="bt-section-header">
                        <h2>Why use our AI Toolbox?</h2>
                    </div>

                    <div className="bt-benefits-grid">
                        <div className="bt-benefit-card">
                            <span className="material-symbols-outlined">thumb_up</span>
                            <h3>Free to use, no signup required</h3>
                            <p>Jump right in and start using all our tools without any registration or hidden fees.</p>
                        </div>
                        <div className="bt-benefit-card">
                            <span className="material-symbols-outlined">bolt</span>
                            <h3>Clean UI, fast results</h3>
                            <p>Our minimalist interface delivers the results you need without unnecessary complexity.</p>
                        </div>
                        <div className="bt-benefit-card">
                            <span className="material-symbols-outlined">smart_toy</span>
                            <h3>Real language models</h3>
                            <p>Powered by sophisticated AI, not simple keyword spinners or basic templates.</p>
                        </div>
                        <div className="bt-benefit-card">
                            <span className="material-symbols-outlined">integration_instructions</span>
                            <h3>Exportable outputs</h3>
                            <p>Easily use your results in your full Book Witch workflow or any other platform.</p>
                        </div>
                    </div>
                </section>

                <section className="bt-cta">
                    <h2>Want more than just small tools?</h2>
                    <p>These mini-tools are just a taste. With <strong>Book Witch</strong>, you can:</p>
                    <ul style={{ 
                        textAlign: 'left', 
                        maxWidth: '500px', 
                        margin: '20px auto 30px', 
                        listStyleType: 'none' 
                    }}>
                        <li style={{ 
                            marginBottom: '10px', 
                            display: 'flex', 
                            alignItems: 'flex-start' 
                        }}>
                            <span className="material-symbols-outlined" style={{ 
                                color: 'var(--color-primary-0)', 
                                marginRight: '10px',
                                fontSize: '20px'
                            }}>check_circle</span>
                            Generate full ebooks from a single prompt
                        </li>
                        <li style={{ 
                            marginBottom: '10px', 
                            display: 'flex', 
                            alignItems: 'flex-start' 
                        }}>
                            <span className="material-symbols-outlined" style={{ 
                                color: 'var(--color-primary-0)', 
                                marginRight: '10px',
                                fontSize: '20px'
                            }}>check_circle</span>
                            Rewrite, edit, and format content at scale
                        </li>
                        <li style={{ 
                            marginBottom: '10px', 
                            display: 'flex', 
                            alignItems: 'flex-start' 
                        }}>
                            <span className="material-symbols-outlined" style={{ 
                                color: 'var(--color-primary-0)', 
                                marginRight: '10px',
                                fontSize: '20px'
                            }}>check_circle</span>
                            Export to PDF, DOCX, or TXT in one click
                        </li>
                    </ul>
                    <div className="bt-cta-buttons">
                        <Link to="/app" className="no-style-link">
                            <button className="button-important">
                                <div className="inner">
                                    Try Book Witch for free
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </div>
                            </button>
                        </Link>
                    </div>
                </section>

                <section className="bt-faq">
                    <h2>FAQ – Free AI Writing Tools</h2>
                    <div className="bt-faq-item">
                        <h3>Are these tools free to use?</h3>
                        <p>Yes, you can use all tools without registration. No hidden fees.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Do I need to sign up?</h3>
                        <p>No account required to test. Just paste your text and go.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Can I use these for professional writing?</h3>
                        <p>Yes! They're perfect for emails, blog posts, academic writing, and more.</p>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default Toolbox; 